import React from "react";
import { Link } from "react-router-dom";
import { useContent } from "../context/ContentContext";
import { SectionTitle } from "../components/VideoCard";
import { Reveal } from "../hooks/useReveal";

export default function About() {
  const { content } = useContent();
  const a = content.about;

  return (
    <div className="bg-[#141414] texture-dark">
      {/* Page hero */}
      <section className="bg-[#d8d8d8] slant-bottom-right relative z-10" data-testid="about-hero">
        <div className="max-w-5xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-28 md:pb-40 text-center">
          <p className="kicker text-neutral-500 mb-5">The Studio</p>
          <h1 className="font-serif-display text-neutral-950 leading-none"
            style={{ fontSize: "clamp(58px, 11vw, 150px)" }} data-testid="about-heading">
            {a.heading}
          </h1>
          <hr className="border-neutral-900/70 max-w-3xl mx-auto mt-2" />
          <h3 className="font-comfortaa text-neutral-900 leading-relaxed mt-8 max-w-3xl mx-auto font-normal"
            style={{ fontSize: "clamp(17px, 2vw, 26px)" }}>
            {a.intro}
          </h3>
        </div>
      </section>

      {/* Story */}
      <section className="texture-dark pt-16 md:pt-20 pb-8" data-testid="about-story-section">
        <div className="max-w-[1240px] mx-auto px-6 md:px-12">
          <Reveal>
            <SectionTitle>{a.storyTitle}</SectionTitle>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <Reveal>
              <div className="space-y-6">
                <p className="font-comfortaa text-white/90 leading-relaxed text-[15px] md:text-base">{a.story1}</p>
                <p className="font-comfortaa text-white/90 leading-relaxed text-[15px] md:text-base">{a.story2}</p>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <img
                src={a.studioImage}
                alt="Inside the studio"
                loading="lazy"
                className="w-full rounded-lg object-cover aspect-[4/3]"
              />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="texture-dark pt-16 md:pt-24 pb-8" data-testid="about-stats-section">
        <div className="max-w-[1240px] mx-auto px-6 md:px-12">
          <Reveal>
            <SectionTitle>{a.statsTitle}</SectionTitle>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {a.stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 90}>
                <div className="text-center md:text-left">
                  <p className="font-comfortaa font-light text-white" style={{ fontSize: "clamp(36px, 4.5vw, 58px)" }}>
                    {s.value}
                  </p>
                  <p className="font-comfortaa text-white/60 text-xs md:text-sm mt-1">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="texture-dark pt-16 md:pt-24 pb-8" data-testid="about-services-section">
        <div className="max-w-[1240px] mx-auto px-6 md:px-12">
          <Reveal>
            <SectionTitle>{a.servicesTitle}</SectionTitle>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
            {a.services.map((s, i) => (
              <Reveal key={s.name} delay={(i % 3) * 90}>
                <div className="border-t border-white/25 pt-5">
                  <p className="font-comfortaa text-white/40 text-xs mb-2">{String(i + 1).padStart(2, "0")}</p>
                  <h4 className="font-comfortaa text-white text-lg">{s.name}</h4>
                  <p className="font-comfortaa text-white/65 text-sm leading-relaxed mt-2">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Tools */}
      <section className="texture-dark pt-16 md:pt-24 pb-16" data-testid="about-tools-section">
        <div className="max-w-[1240px] mx-auto px-6 md:px-12">
          <Reveal>
            <SectionTitle>{a.toolsTitle}</SectionTitle>
          </Reveal>
          <Reveal>
            <div className="flex flex-wrap gap-3">
              {a.tools.map((t) => (
                <span
                  key={t}
                  className="font-comfortaa text-xs md:text-sm text-white/85 border border-white/30 rounded-full px-4 py-2 hover:border-white/80 hover:text-white"
                  style={{ transition: "border-color 0.25s ease, color 0.25s ease" }}
                >
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal className="text-center mt-20">
            <Link to="/contact" className="pill-button inline-block" data-testid="about-contact-button">
              Contact Us
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
